import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useSelect } from 'downshift';
import React, { useState } from 'react';
import { Input } from 'ui';
import { postCreateModel } from '~/api/listFlow';
import ErrorMessage from '~/components/Form/ErrorMessage';
import { SelectorIcon } from '~/components/Icons/SelectorIcon';
import SearchIcon from '~/public/static/svg/SearchIcon';
import { RailsItemModel } from '~/typings/services/rails/item';

interface Props {
  categoryId?: number;
  clearable?: boolean;
  brandId?: number;

  // Greys out and disables the selector
  disabled?: boolean;

  id: string;

  models?: RailsItemModel[];
  /**
   * Fired when selection changes
   * @param model Selected model. Can be "undefined" because it should be clearable
   */
  onChange: (model: RailsItemModel | null) => void;

  // Overrides the placeholder
  placeholder?: string;

  // Pass the full model object
  value?: RailsItemModel | null;
}

const ModelSelector: React.FC<Props> = ({
  brandId,
  categoryId,
  clearable,
  disabled,
  id,
  models,
  onChange,
  placeholder,
  value,
}) => {
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');

  const filteredModels = inputValue
    ? models?.filter(m =>
        m.name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : models;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    setHighlightedIndex,
    closeMenu,
    reset,
  } = useSelect({
    id,
    items: filteredModels || [],
    itemToString: item => item?.name || '',
    onSelectedItemChange: changes => onChange(changes.selectedItem || null),
    stateReducer: (state, changes) => {
      // Ignore space button
      if (
        changes.type === useSelect.stateChangeTypes.MenuKeyDownSpaceButton ||
        changes.key === 'Space'
      ) {
        setInputValue(p => p + ' ');
        return state;
      }
      return changes.changes;
    },
  });

  const {
    mutate: createModel,
    error,
    isLoading,
  } = useMutation(
    () => {
      if (!brandId) {
        throw new Error('Must have a brand selected');
      }

      if (!categoryId) {
        throw new Error('Must have a category selected');
      }

      return postCreateModel({
        name: inputValue,
        brandId,
        categoryId,
      });
    },
    {
      onSuccess: newModel => {
        queryClient.invalidateQueries(['list-flow-models']);
        onChange(newModel);
        closeMenu();
      },
    },
  );

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    setHighlightedIndex(0);
  }

  function handleReset() {
    reset();
    onChange(null);
    setInputValue('');
  }

  return (
    <div className="relative">
      <div
        {...getToggleButtonProps()}
        className={clsx(
          'flex w-full cursor-pointer items-center focus-within:border-green-500 focus-within:ring',
          isOpen
            ? 'rounded-t-md border-l border-r border-t'
            : 'rounded-md border',
        )}
      >
        <button
          className={clsx(
            'flex-1 rounded px-4 py-2 text-left outline-none',
            value ? 'font-semibold' : '',
          )}
          type="button"
        >
          {value?.name || (
            <span className="text-grey-700">
              {placeholder || 'Select a model'}
            </span>
          )}
        </button>

        {!!value && clearable && (
          <button
            type="button"
            className="p-2"
            onClick={() => {
              handleReset();
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        <button type="button" tabIndex={-1} className="py-2.5 pl-1 pr-2">
          <SelectorIcon />
        </button>
      </div>

      <ul
        {...getMenuProps()}
        className="absolute top-full z-20 w-full overflow-y-scroll rounded-b border-b border-l border-r bg-white shadow-lg"
        style={{
          visibility: isOpen ? 'visible' : 'collapse',
          maxHeight: 400,
        }}
      >
        {isOpen && (
          <>
            <Input
              leftAdornment={
                <SearchIcon
                  className="text-slate-green-500 ml-2"
                  fill="currentColor"
                />
              }
              onFocus={e => e.target.select()}
              type="search"
              inputClassName="!py-4 rounded-sm"
              className="mx-4 mb-4 mt-2 rounded-sm"
              placeholder="Search models..."
              onChange={handleInputChange}
              value={inputValue}
            />

            {filteredModels?.map((model, i) => (
              <li
                key={model.id}
                {...getItemProps({
                  item: model,
                  index: i,
                })}
                className={clsx(
                  'px-4 py-2 md:py-1',
                  highlightedIndex === i && 'bg-pastel-green-500',
                )}
              >
                {model.name}
              </li>
            ))}

            {inputValue && !!categoryId && !!brandId && (
              <>
                <div className="px-4 pb-4">
                  <ErrorMessage errors={error} />

                  {isLoading ? (
                    <div className="bg-mint-green-500 w-full rounded px-4 py-2 text-left">
                      Creating...
                    </div>
                  ) : (
                    <button
                      className="bg-mint-green-500 w-full rounded px-4 py-2 text-left"
                      onClick={() => createModel()}
                      type="button"
                    >
                      Create new model &ldquo;{inputValue}&rdquo;
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

export default ModelSelector;
